import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch, getCSRFToken } from "./utils"

export interface GeographicExposureData {
  geographic_region: string
  qpcsp_weighting: number
  equity: number
  venture_capital: number
  real_assets: number
  credit: number
  is_total?: boolean
}

export interface SectorExposureData {
  sector: string
  s_p_500: number | null
  qpcsp_weighting: number
  equity: number
  venture_capital: number
  real_assets: number
  credit: number
  is_total?: boolean
}

export interface SubSectorExposureData {
  sub_sector: string
  qpcsp_weighting: number
  equity: number
  venture_capital: number
  real_assets: number
  credit: number
  is_total?: boolean
}

export interface PortfolioCompany {
  id: string
  display_name: string
  logo_url: string | null
  realized: boolean
  liquidated: boolean
  new_this_quarter: boolean
  gics_sector: string | null
  gics_subsector: string | null
  geographic_region: string | null
}

export interface HistoricalPnLByVintageRow {
  vintage: string
  values: number[]
  is_total?: boolean
}

export interface HistoricalPnLByVintageData {
  quarters: string[]
  rows: HistoricalPnLByVintageRow[]
}

export interface HistoricalPnLByAssetClassRow {
  asset_class: string
  values: number[]
  is_total?: boolean
}

export interface HistoricalPnLByAssetClassData {
  quarters: string[]
  rows: HistoricalPnLByAssetClassRow[]
}

export interface HistoricalIrrByAssetClassRow {
  asset_class: string
  values: (number | null)[]
  is_total?: boolean
}

export interface HistoricalIrrByAssetClassData {
  quarters: string[]
  rows: HistoricalIrrByAssetClassRow[]
}

export interface HistoricalIrrByVintageRow {
  vintage: string
  values: (number | null)[]
  is_total?: boolean
}

export interface HistoricalIrrByVintageData {
  quarters: string[]
  rows: HistoricalIrrByVintageRow[]
}

export interface HistoricalPnLBySeriesRow {
  series: string
  values: number[]
  is_total?: boolean
}

export interface HistoricalPnLBySeriesData {
  quarters: string[]
  rows: HistoricalPnLBySeriesRow[]
}

export interface HistoricalIrrBySeriesRow {
  series: string
  values: (number | null)[]
  is_total?: boolean
}

export interface HistoricalIrrBySeriesData {
  quarters: string[]
  rows: HistoricalIrrBySeriesRow[]
}

export interface CachedFieldDebug {
  output: any
  metadata?: {
    generated_at?: string
    service?: string
    model?: string
    prompts?: {
      system?: string
      user?: string
    }
    context?: Record<string, any>
    input_data?: Record<string, any>
  }
  raw: any
}

export interface DebugData {
  cached_data: {
    attribution_highlights: CachedFieldDebug
    liquidity_events: CachedFieldDebug
    new_investments: CachedFieldDebug
    cash_flows: CachedFieldDebug
    high_level_summary: CachedFieldDebug
    performance_by_series: { output: any; raw: any }
    performance_by_stage: { output: any; raw: any }
    performance_by_asset_class: { output: any; raw: any }
    top_ten_portfolio_companies: { output: any; raw: any }
    geographic_exposure: { output: any; raw: any }
    sector_exposure: { output: any; raw: any }
  }
}

export interface QuarterlyReport {
  id: string
  household_id: string
  household_name: string
  status: "pending" | "generating" | "completed" | "failed"
  created_at: string
  updated_at: string
  sections?: string[]
  debug?: DebugData
  section_content?: {
    editor_data_content?: {
      irr_series_data?: any
      performance_by_stage_data?: any
      performance_by_series_data?: any
      performance_by_asset_class_data?: any
      performance_by_vintage_data?: any
      performance_by_lp_entity_data?: any
      top_ten_portfolio_companies_data?: any
      top_five_series_data?: any
      geographic_exposure_data?: GeographicExposureData[]
      sector_exposure_data?: SectorExposureData[]
      sp500_data_as_of_date?: string | null
      sub_sector_exposure_data?: SubSectorExposureData[]
      portfolio_companies?: PortfolioCompany[]
      historical_pnl_by_vintage_data?: HistoricalPnLByVintageData
      historical_pnl_by_asset_class_data?: HistoricalPnLByAssetClassData
      historical_irr_by_asset_class_data?: HistoricalIrrByAssetClassData
      historical_irr_by_vintage_data?: HistoricalIrrByVintageData
      historical_pnl_by_series_data?: HistoricalPnLBySeriesData
      historical_irr_by_series_data?: HistoricalIrrBySeriesData
    }
    editor_ai_content?: {
      attribution_highlights?: string[]
      liquidity_events?: string[]
      new_investments?: string[]
      cash_flows?: string
      high_level_summary?: string
    }
    lp_high_level_kpis_and_highlights?: {
      editor_content?: any
    }
    qpcsp_intro_letter?: {
      editor_content?: any
    }
    lp_capital_statement?: {}
    lp_investment_performance?: {
      editor_content?: any
    }
    attribution_exposure_letter?: {
      editor_content?: any
    }
    exposure_by_sector?: {
      editor_content?: any
    }
    exposure_by_geography?: {
      editor_content?: any
    }
    logos_company_counts?: {
      editor_content?: any
    }
    historical_investment_performance?: {
      editor_content?: any
      historical_pnl_by_vintage_data?: HistoricalPnLByVintageData
      historical_pnl_by_asset_class_data?: HistoricalPnLByAssetClassData
      historical_irr_by_asset_class_data?: HistoricalIrrByAssetClassData
      historical_irr_by_vintage_data?: HistoricalIrrByVintageData
      historical_pnl_by_series_data?: HistoricalPnLBySeriesData
      historical_irr_by_series_data?: HistoricalIrrBySeriesData
    }
    qpcsp_financial_statement?: {}
  }
}
export interface FetchQuarterlyReportsResponse {
  reports: QuarterlyReport[]
  all_ready?: boolean
}

export const fetchQuarterlyReports = async (
  communicationId: string,
): Promise<FetchQuarterlyReportsResponse> => {
  const url = `/api/household_quarterly_reports?communication_id=${communicationId}`
  return apiFetch<FetchQuarterlyReportsResponse>(url)
}

export const fetchQuarterlyReport = async (
  id: string,
  options?: { mock?: boolean; debug?: boolean },
): Promise<QuarterlyReport> => {
  const params = new URLSearchParams()
  if (options?.mock) params.append("mock", "true")
  if (options?.debug) params.append("debug", "true")

  const url = `/api/household_quarterly_reports/${id}${
    params.toString() ? `?${params.toString()}` : ""
  }`
  return apiFetch<QuarterlyReport>(url)
}

export const useQuarterlyReports = (
  quarterlyReportId: string,
  options?: {
    refetchInterval?:
      | number
      | false
      | ((data: FetchQuarterlyReportsResponse | undefined) => number | false)
  },
) => {
  return useQuery<FetchQuarterlyReportsResponse, Error>({
    queryKey: ["household_quarterly_reports", quarterlyReportId],
    queryFn: () => fetchQuarterlyReports(quarterlyReportId),
    refetchInterval: options?.refetchInterval,
  })
}

export interface UpdateQuarterlyReportParams {
  id: string
  sections?: string[]
}

export interface UpdateQuarterlyReportContentParams {
  id: string
  lp_highlevel_kpis_and_highlights_editorjs_content?: any
  lp_investment_performance_editorjs_content?: any
  attribution_exposure_letter_editorjs_content?: any
  historical_investment_performance_editorjs_content?: any
  exposure_by_geography_editorjs_content?: any
  exposure_by_sector_editorjs_content?: any
  logos_company_counts_editorjs_content?: any
}

export const updateQuarterlyReport = async (
  params: UpdateQuarterlyReportParams,
): Promise<QuarterlyReport> => {
  const url = `/api/household_quarterly_reports/${params.id}`
  return apiFetch<QuarterlyReport>(url, {
    method: "PATCH",
    body: JSON.stringify({
      household_quarterly_report: {
        sections: params.sections,
      },
    }),
  })
}

export const useQuarterlyReport = (
  id: string,
  options?: { mock?: boolean; debug?: boolean },
) => {
  return useQuery<QuarterlyReport, Error>({
    queryKey: ["household_quarterly_report", id, options?.mock, options?.debug],
    queryFn: () => fetchQuarterlyReport(id, options),
    enabled: !!id,
  })
}

export const updateQuarterlyReportContent = async (
  params: UpdateQuarterlyReportContentParams,
): Promise<QuarterlyReport> => {
  const url = `/api/household_quarterly_reports/${params.id}`
  return apiFetch<QuarterlyReport>(url, {
    method: "PATCH",
    body: JSON.stringify({
      household_quarterly_report: {
        lp_highlevel_kpis_and_highlights_editorjs_content:
          params.lp_highlevel_kpis_and_highlights_editorjs_content,
        lp_investment_performance_editorjs_content:
          params.lp_investment_performance_editorjs_content,
        attribution_exposure_letter_editorjs_content:
          params.attribution_exposure_letter_editorjs_content,
        historical_investment_performance_editorjs_content:
          params.historical_investment_performance_editorjs_content,
        exposure_by_geography_editorjs_content:
          params.exposure_by_geography_editorjs_content,
        exposure_by_sector_editorjs_content:
          params.exposure_by_sector_editorjs_content,
        logos_company_counts_editorjs_content:
          params.logos_company_counts_editorjs_content,
      },
    }),
  })
}

export const useUpdateQuarterlyReport = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateQuarterlyReport,
    onSuccess: (_, variables) => {
      // Invalidate the report query to refresh data
      queryClient.invalidateQueries({
        queryKey: ["household_quarterly_report", variables.id],
      })
    },
  })
}

export const useUpdateQuarterlyReportContent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateQuarterlyReportContent,
    onSuccess: (_, variables) => {
      // Invalidate the report query to refresh data
      queryClient.invalidateQueries({
        queryKey: ["household_quarterly_report", variables.id],
      })
    },
  })
}

export const deleteHouseholdQuarterlyReport = async (
  id: string,
): Promise<{ success: boolean; message: string }> => {
  return apiFetch<{ success: boolean; message: string }>(
    `/api/household_quarterly_reports/${id}`,
    {
      method: "DELETE",
    },
  )
}

export const useDeleteHouseholdQuarterlyReport = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteHouseholdQuarterlyReport(id),
    onSuccess: () => {
      // Invalidate the household quarterly reports query to refresh data
      queryClient.invalidateQueries({
        queryKey: ["household_quarterly_reports"],
      })
    },
  })
}

export const regenerateHouseholdQuarterlyReport = async (
  id: string,
): Promise<{ success: boolean; message: string }> => {
  return apiFetch<{ success: boolean; message: string }>(
    `/api/household_quarterly_reports/${id}/regenerate`,
    {
      method: "POST",
    },
  )
}

export const useRegenerateHouseholdQuarterlyReport = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => regenerateHouseholdQuarterlyReport(id),
    onSuccess: () => {
      // Invalidate the household quarterly reports query to refresh data
      queryClient.invalidateQueries({
        queryKey: ["household_quarterly_reports"],
      })
    },
  })
}

export const sendHouseholdQuarterlyReport = async (
  id: string,
): Promise<Blob> => {
  const response = await fetch(
    `/api/household_quarterly_reports/${id}/send_report`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
      credentials: "include",
    },
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: "Failed to generate PDF",
    }))
    throw new Error(error.error || "Failed to generate PDF")
  }

  return response.blob()
}

export const useSendHouseholdQuarterlyReport = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      // Instead of fetching and creating a blob, just open the URL directly
      // The backend sends with disposition: inline, so it should open in browser
      const url = `/api/household_quarterly_reports/${id}/send_report`

      // Open in new tab - browser will handle the inline PDF
      const newWindow = window.open(url, "_blank")

      if (!newWindow) {
        console.error(
          "Popup blocked! User needs to allow popups for this site.",
        )
        throw new Error(
          "Popup blocked. Please allow popups for this site to view the PDF.",
        )
      }

      return { success: true }
    },
    onError: (error) => {
      console.error("PDF generation error:", error)
    },
  })
}

// Capital Statements

export interface CapitalStatementDocument {
  id: string
  name: string
  filename: string
  file_url?: string
}

export interface CapitalStatementEntity {
  id: string
  name: string
  document: CapitalStatementDocument | null
}

export interface CapitalStatementsResponse {
  entities: CapitalStatementEntity[]
  missing_entities: { id: string; name: string }[]
}

export const fetchCapitalStatements = async (
  reportId: string,
): Promise<CapitalStatementsResponse> => {
  return apiFetch<CapitalStatementsResponse>(
    `/api/household_quarterly_reports/${reportId}/capital_statements`,
  )
}

export const useCapitalStatements = (reportId: string) => {
  return useQuery<CapitalStatementsResponse, Error>({
    queryKey: ["capital_statements", reportId],
    queryFn: () => fetchCapitalStatements(reportId),
    enabled: !!reportId,
  })
}

export const uploadCapitalStatement = async (
  reportId: string,
  investorEntityId: string,
  signedId: string,
): Promise<CapitalStatementEntity> => {
  return apiFetch<CapitalStatementEntity>(
    `/api/household_quarterly_reports/${reportId}/upload_capital_statement`,
    {
      method: "POST",
      body: JSON.stringify({
        investor_entity_id: investorEntityId,
        signed_id: signedId,
      }),
    },
  )
}

export const useUploadCapitalStatement = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      reportId,
      investorEntityId,
      signedId,
    }: {
      reportId: string
      investorEntityId: string
      signedId: string
    }) => uploadCapitalStatement(reportId, investorEntityId, signedId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["capital_statements", variables.reportId],
      })
    },
  })
}
